import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import Markdown from "@/components/Markdown";
import SEOHead from "@/components/SEOHead";
import SeoImage from "@/components/SeoImage";
import { articleJsonLd, breadcrumbJsonLd } from "@/lib/schema";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setErr(true)); }, [slug]);

  if (err) return (
    <div className="mx-auto max-w-3xl px-6 py-24">
      <p>Entrada no encontrada.</p>
      <Link to="/blog" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[hsl(var(--clinic-secondary))]"><ArrowLeft size={14} /> Volver al blog</Link>
    </div>
  );
  if (!post) return <div className="mx-auto max-w-3xl px-6 py-24">Cargando…</div>;

  return (
    <>
      <SEOHead
        title={`${post.title} · Katia Falón Odontología`}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        jsonLd={[articleJsonLd(post), breadcrumbJsonLd([{name:"Inicio",path:"/"},{name:"Blog",path:"/blog"},{name:post.title,path:`/blog/${post.slug}`}])]}
      />
      <article data-testid={`blog-post-${slug}`} className="mx-auto max-w-3xl px-6 py-16 lg:py-24">
        <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-1 text-sm text-[hsl(var(--clinic-muted))] hover:text-[hsl(var(--clinic-text))]">
          <ArrowLeft size={14} /> Blog
        </Link>
        <div className="mt-4 text-xs uppercase tracking-[0.3em] text-[hsl(var(--clinic-muted))]">
          {new Date(post.created_at).toLocaleDateString("es-CO", { year: "numeric", month: "long", day: "numeric" })} · {post.author}
        </div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl">{post.title}</h1>
        <p className="mt-4 text-lg text-[hsl(var(--clinic-secondary))]">{post.excerpt}</p>
        {post.cover_slug && (
          <div className="mt-8 overflow-hidden rounded-2xl">
            <SeoImage
              slug={post.cover_slug}
              alt={post.cover_alt || post.title}
              caption={post.cover_alt}
              schema
              eager
              className="aspect-[16/9] w-full object-cover"
              testId={`blog-post-cover-${slug}`}
            />
          </div>
        )}
        <div className="mt-8">
          <Markdown testId="blog-body">{post.body_markdown}</Markdown>
        </div>
        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-[hsl(var(--clinic-border))] pt-8">
          <div className="flex flex-wrap gap-2">
            {(post.tags || []).map((t) => (
              <span key={t} className="rounded-full bg-[hsl(var(--clinic-bg))] px-3 py-1 text-xs text-[hsl(var(--clinic-muted))]">{t}</span>
            ))}
          </div>
          <Link to="/reservar" data-testid="blog-cta-book" className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--clinic-secondary))] px-5 py-2.5 text-sm font-semibold text-white">
            Reserva tu cita <ArrowRight size={14} />
          </Link>
        </div>
      </article>
    </>
  );
}
